// src/components/LocationTermsModal/LocationTermsModal.jsx
"use client"

import { useState, useEffect } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import usersApiClient from "../../services/usersApi";
import { getUserData, saveUserData } from "../../utils/auth";
import { useLocations } from "../../utils/LocationContext";
import { useAuth } from "../../utils/AuthContext";
import LegalModal from "../LegalAspects/LegalModal";
import GeolocationField from "../GeolocationField/GeolocationField";
import "./LocationTermsModal.css";

const LocationTermsModal = ({
                                isOpen,
                                onClose,
                                onSuccess,
                                isMandatory = false,
                                title = "Completa tu información",
                                description = "Agregar tu ubicación nos permite mostrarte:",
                                benefits = [
                                    "Servicios disponibles en tu zona",
                                    "Profesionales recomendados en tu localidad"
                                ]
                            }) => {
    const { locations: prefetchedLocations, loadingLocations } = useLocations();
    const { setUserData } = useAuth();
    const [location, setLocation] = useState("");
    const [locationCoordinates, setLocationCoordinates] = useState(null);
    const [otherLocation, setOtherLocation] = useState("");
    const [termsAccepted, setTermsAccepted] = useState(false);
    // Locations vienen prefetcheadas del LocationContext (login/app start).
    const availableLocations = prefetchedLocations;
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    // Cargar datos existentes del usuario cuando las ubicaciones estén listas
    useEffect(() => {
        if (isOpen && availableLocations.length > 0) {
            const userData = getUserData();
            if (userData) {
                if (userData.location) {
                    // Buscar si la ubicación del usuario existe en la lista de ubicaciones disponibles
                    const existingLocation = availableLocations.find(loc =>
                        (typeof loc === 'string' ? loc : loc.name) === userData.location
                    );

                    if (!existingLocation) {
                        setLocation("custom");
                        setOtherLocation(userData.location);
                    } else {
                        setLocation(userData.location);
                        setOtherLocation("");
                    }
                }
                if (userData.termsAccepted === true) {
                    setTermsAccepted(true);
                }
            }
        }
    }, [isOpen, availableLocations]);

    // Prevenir cierre del modal con ESC si es mandatory
    useEffect(() => {
        if (isOpen && isMandatory) {
            const handleKeyDown = (e) => {
                if (e.key === 'Escape') {
                    e.preventDefault();
                    toast.warning("Debes completar tu información antes de continuar.");
                }
            };

            document.addEventListener('keydown', handleKeyDown);
            return () => document.removeEventListener('keydown', handleKeyDown);
        }
    }, [isOpen, isMandatory]);

    // Navegación del navegador para mandatory
    useEffect(() => {
        if (isOpen && isMandatory) {
            const handleBeforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = "Debes completar tu ubicación y aceptar los términos antes de salir.";
                return "Debes completar tu ubicación y aceptar los términos antes de salir.";
            };

            const handlePopState = (e) => {
                const shouldStay = window.confirm("Debes completar tu ubicación y aceptar los términos antes de continuar. ¿Estás seguro de que quieres salir?");
                if (!shouldStay) {
                    window.history.pushState(null, null, window.location.pathname);
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('popstate', handlePopState);
            window.history.pushState(null, null, window.location.pathname);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [isOpen, isMandatory]);

    const handleLocationChange = (newLocation) => {
        setLocation(newLocation);
        if (newLocation !== "custom") {
            setOtherLocation("");
        }
    };

    const handleCoordinatesChange = (coordinates) => {

        setLocationCoordinates(coordinates);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevenir el comportamiento por defecto del formulario



        if (!location || (location === "custom" && !otherLocation.trim())) {
            console.log('❌ Validación falló: ubicación requerida');
            toast.error("Por favor selecciona una ubicación");
            return;
        }

        if (!termsAccepted) {
            console.log('❌ Validación falló: términos no aceptados');
            toast.error("Debes aceptar los términos y condiciones");
            return;
        }

        setLoadingSubmit(true);

        try {
            const finalLocationName = location === "custom" ? otherLocation.trim() : location;

            // Construir payload según la documentación del backend para PUT /users/location
            let locationPayload = {
                termsAccepted: true,
                useSmartLocation: true
            };


            if (locationCoordinates) {

                locationPayload.locationType = locationCoordinates.type === 'approximate' ? 'GPS' : 'GPS';
                locationPayload.displayName = finalLocationName;
                locationPayload.coordinates = {
                    lat: locationCoordinates.latitude,
                    lng: locationCoordinates.longitude
                };
                locationPayload.gpsPermissionGranted = true;


            } else if (location && location !== "custom") {


                locationPayload.locationType = "ZONE_SELECTION";

                // Buscar el ID de la ubicación en la lista disponible
                const selectedLocationObj = availableLocations.find(loc =>
                    (typeof loc === 'string' ? loc : loc.name) === location
                );

                locationPayload.selectedZoneId = selectedLocationObj?.id || location;


            } else if (location === "custom") {

                locationPayload.locationType = "CUSTOM_INPUT";
                locationPayload.customLocation = finalLocationName;


            } else {
                locationPayload = {
                    location: finalLocationName,
                    termsAccepted: true,
                    useSmartLocation: false
                };

            }


            // Usar el endpoint unificado /location en lugar de /smart-location
            const response = await usersApiClient.put('/location', locationPayload);


            // Actualizar userData local
            const userData = getUserData();

            if (userData) {
                const updatedUserData = {
                    ...userData,
                    location: finalLocationName, // Compatibilidad
                    termsAccepted: true // Marcar términos como aceptados
                };

                // Si la respuesta incluye información de ubicación inteligente, guardarla
                if (response.data.serviceZone || response.data.hasCoordinates) {
                    updatedUserData.smartLocation = {
                        displayName: response.data.displayLocation || finalLocationName,
                        serviceZone: response.data.serviceZone,
                        hasCoordinates: response.data.hasCoordinates
                    };
                }

                saveUserData(updatedUserData);
                setUserData(updatedUserData);
            }

            toast.success(`📍 Ubicación actualizada: ${finalLocationName}`);

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error("❌ ERROR EN LOCATIONTERMSMODAL:");
            console.error("📍 Tipo de error:", error.name);
            console.error("📝 Mensaje:", error.message);
            console.error("📊 Status HTTP:", error.response?.status);
            console.error("📦 Data del error:", error.response?.data);
            console.error("🔗 URL:", error.config?.url);
            console.error("📤 Método:", error.config?.method);
            console.error("📦 Payload enviado:", error.config?.data);
            console.error("🕐 Timestamp error:", new Date().toISOString());
            console.error("🔍 Stack trace:", error.stack);

            // Mostrar mensaje de error más específico
            const errorMessage = error.response?.data?.error || "Error al actualizar la ubicación";
            console.log("💬 Mensaje mostrado al usuario:", errorMessage);
            toast.error(errorMessage);
        } finally {
            setLoadingSubmit(false);
        }
    };

    const handleOpenTermsModal = (e) => {
        e.preventDefault();
        setShowTermsModal(true);
    };

    const handleCloseTermsModal = () => {
        setShowTermsModal(false);
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            if (isMandatory) {
                toast.warning("Debes completar tu información antes de continuar.");
            } else {
                onClose && onClose();
            }
        }
    };

    const getSubmitButtonText = () => {
        if (loadingSubmit) return "Guardando...";
        if (location === "Otra" && !otherLocation) return "Ingresa tu ubicación";
        if (!location) return "Selecciona una ubicación";
        if (!termsAccepted) return "Acepta los términos para continuar";
        if (showTermsModal) return "Cierra los términos para continuar";
        return "Confirmar información";
    };

    const isSubmitDisabled = () => {
        if (loadingLocations || loadingSubmit || !termsAccepted || showTermsModal) return true;
        if (!location) return true;
        if (location === "Otra" && !otherLocation.trim()) return true;
        return false;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="ltm-modal-overlay" onClick={handleOverlayClick}>
                <div className="ltm-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="ltm-modal-content">
                        <div className="ltm-modal-header">
                            <div className="ltm-modal-icon-wrapper">
                                <FaMapMarkerAlt className="ltm-modal-icon" />
                            </div>
                            <h2 className="ltm-modal-title">{title}</h2>
                        </div>

                        <div className="ltm-modal-description">
                            <p>{description}</p>
                            <ul className="ltm-benefits">
                                {benefits.map((benefit, index) => (
                                    <li key={index}>{benefit}</li>
                                ))}
                            </ul>
                        </div>

                        <form onSubmit={handleSubmit} className="ltm-modal-form">
                            <div className="ltm-form-group">
                                <label htmlFor="modal-location" className="ltm-form-label">
                                    Selecciona tu ubicación
                                </label>

                                <GeolocationField
                                    value={location}
                                    onChange={handleLocationChange}
                                    onCoordinatesChange={handleCoordinatesChange}
                                    label="Ubicación"
                                    required={true}
                                    allowCustom={true}
                                />
                            </div>

                            <div className="ltm-terms-section">
                                <label className="ltm-terms-checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="ltm-terms-checkbox"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                        disabled={showTermsModal}
                                    />
                                    <span className="ltm-checkmark"></span>
                                    <span className="ltm-terms-text">
                                        Acepto los{" "}
                                        <button
                                            type="button"
                                            onClick={handleOpenTermsModal}
                                            className="ltm-terms-link"
                                        >
                                            Términos y Condiciones
                                        </button>
                                    </span>
                                </label>
                            </div>

                            {isMandatory && (
                                <p className="ltm-required-info-text">
                                    * Información requerida para continuar
                                </p>
                            )}

                            <button
                                type="submit"
                                className={`ltm-submit-button ${isSubmitDisabled() ? 'disabled' : ''}`}
                                disabled={isSubmitDisabled()}
                            >
                                {getSubmitButtonText()}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {showTermsModal && (
                <LegalModal
                    isOpen={showTermsModal}
                    onClose={handleCloseTermsModal}
                    title="Términos y Condiciones"
                    contentType="terms"
                />
            )}
        </>
    );
};

export default LocationTermsModal;
